import React from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Building2, 
  Store, 
  Users, 
  Shield, 
  Package, 
  Tags, 
  Ruler,
  Truck,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  BarChart3,
  LogOut,
  Menu,
  X,
  Bell,
  Zap,
  Settings,
  HelpCircle,
  ClipboardList,
  ClipboardCheck,
  History,
  Home,
  MoreHorizontal,
  UserCircle,
  Wallet,
  FileText,
  Briefcase,
  FileStack,
  Utensils,
  CreditCard,
  ChefHat
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { getInitials } from '../lib/utils';
import { cn } from '../lib/utils';

const navItems = [
  { 
    label: 'Dashboard', 
    path: '/', 
    icon: LayoutDashboard 
  },
  { 
    label: 'Help Guide', 
    path: '/help', 
    icon: HelpCircle
  },
  {
    type: 'divider',
    label: 'Quick Actions'
  },
  { 
    label: 'Quick Issue', 
    path: '/quick-entry', 
    icon: Zap,
    highlight: true
  },
  { 
    label: 'Quick Inward', 
    path: '/quick-inward', 
    icon: ArrowDownToLine,
    highlight: true
  },
  { 
    label: 'Quick Transfer', 
    path: '/quick-transfer', 
    icon: ArrowLeftRight,
    highlight: true
  },
  {
    type: 'divider',
    label: 'Masters'
  },
  { 
    label: 'Properties', 
    path: '/properties', 
    icon: Building2 
  },
  { 
    label: 'Stores', 
    path: '/stores', 
    icon: Store 
  },
  { 
    label: 'Items', 
    path: '/items', 
    icon: Package 
  },
  { 
    label: 'Categories', 
    path: '/categories', 
    icon: Tags 
  },
  { 
    label: 'Units', 
    path: '/units', 
    icon: Ruler 
  },
  { 
    label: 'Service Items', 
    path: '/service-items', 
    icon: Briefcase 
  },
  { 
    label: 'Vendors', 
    path: '/vendors', 
    icon: Truck 
  },
  { 
    type: 'divider',
    label: 'Stock Operations'
  },
  { 
    label: 'Stock Inward', 
    path: '/stock-inward', 
    icon: ArrowDownToLine 
  },
  { 
    label: 'Stock Issue', 
    path: '/stock-issue', 
    icon: ArrowUpFromLine 
  },
  { 
    label: 'Indents / Requisitions', 
    path: '/indents', 
    icon: FileText 
  },
  { 
    label: 'Stock Transfer', 
    path: '/stock-transfer', 
    icon: ArrowLeftRight 
  },
  { 
    label: 'Current Stock', 
    path: '/stock', 
    icon: Package 
  },
  { 
    label: 'Adjustment History', 
    path: '/stock-adjustments', 
    icon: History 
  },
  { 
    label: 'Stock Acknowledgment', 
    path: '/stock-acknowledgment', 
    icon: ClipboardCheck 
  },
  { 
    type: 'divider',
    label: 'Consumption'
  },
  { 
    label: 'Consumption Log', 
    path: '/consumption', 
    icon: Utensils,
    highlight: true
  },
  {
    type: 'divider',
    label: 'Kitchen'
  },
  { 
    label: 'Menu PO Assistant', 
    path: '/menu-po', 
    icon: ChefHat,
    highlight: true
  },
  { 
    type: 'divider',
    label: 'Procurement'
  },
  { 
    label: 'Purchase Orders', 
    path: '/purchase-orders', 
    icon: ClipboardList 
  },
  { 
    label: 'Goods Receipt (GRN)', 
    path: '/grn', 
    icon: ClipboardCheck 
  },
  { 
    label: 'Service Receipt (SRN)', 
    path: '/srn', 
    icon: Briefcase 
  },
  { 
    label: 'Service Templates', 
    path: '/service-templates', 
    icon: FileStack 
  },
  { 
    label: 'Clients & Events', 
    path: '/clients', 
    icon: UserCircle 
  },
  { 
    label: 'Petty Cash', 
    path: '/petty-cash', 
    icon: Wallet 
  },
  { 
    label: 'Payments', 
    path: '/payments', 
    icon: CreditCard,
    highlight: true
  },
  { 
    type: 'divider',
    label: 'Reports'
  },
  { 
    label: 'Reports', 
    path: '/reports', 
    icon: BarChart3 
  },
  { 
    type: 'divider',
    label: 'Administration'
  },
  { 
    label: 'Users', 
    path: '/users', 
    icon: Users 
  },
  { 
    label: 'Roles', 
    path: '/roles', 
    icon: Shield 
  },
  { 
    label: 'Audit Logs', 
    path: '/audit-logs', 
    icon: History,
    superAdminOnly: true
  },
  { 
    label: 'Bank Analytics', 
    path: '/bank-analytics', 
    icon: BarChart3,
    superAdminOnly: true
  },
  { 
    label: 'App Settings', 
    path: '/settings', 
    icon: Settings,
    superAdminOnly: true
  },
];

export const DashboardLayout = () => {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="spinner h-8 w-8" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-40 w-[280px] lg:w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="p-4 lg:p-6 border-b border-border flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
            <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-base lg:text-lg font-['Playfair_Display']">H</span>
            </div>
            <div>
              <h1 className="font-semibold text-foreground text-base lg:text-lg leading-tight">Happy Retreats</h1>
              <p className="text-xs text-muted-foreground">Inventory System</p>
            </div>
          </Link>
          {/* Close button for mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin">
          <ul className="space-y-1">
            {navItems.map((item, index) => {
              if (item.type === 'divider') {
                return (
                  <li key={index} className="pt-4 pb-2 px-4">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {item.label}
                    </span>
                  </li>
                );
              }

              // Hide superAdminOnly items for non-super admins
              if (item.superAdminOnly && user?.role_name !== 'Super Admin') {
                return null;
              }

              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "sidebar-item",
                      isActive && "sidebar-item-active",
                      item.highlight && !isActive && "bg-primary/10 text-primary hover:bg-primary/20"
                    )}
                    data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                  >
                    <Icon className={cn("h-5 w-5 flex-shrink-0", item.highlight && "text-primary")} />
                    <span className="truncate">{item.label}</span>
                    {item.highlight && !isActive && (
                      <span className="ml-auto text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                        FAST
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                {getInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.role_name || 'User'}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-lg border-b border-stone-200 h-16">
          <div className="h-full flex items-center justify-between px-4 lg:px-8">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden h-10 w-10 flex items-center justify-center"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <h2 className="text-base lg:text-lg font-semibold text-foreground truncate max-w-[180px] sm:max-w-none">
                {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-10 w-10 flex items-center justify-center" data-testid="notifications-btn">
                <Bell className="h-5 w-5" />
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 h-10 px-2 sm:px-3" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium truncate max-w-[100px]">{user?.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user?.full_name}</span>
                      <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 pb-20 lg:pb-8">
          <Outlet />
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-stone-200 z-50 lg:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="h-16 flex items-center justify-around px-2">
            <Link
              to="/"
              className={cn(
                "flex flex-col items-center justify-center h-14 min-w-[60px] rounded-lg transition-all active:scale-95",
                location.pathname === '/' ? "text-primary" : "text-muted-foreground"
              )}
            >
              <Home className="h-5 w-5" />
              <span className="text-[10px] mt-1 font-medium">Home</span>
            </Link>
            <Link
              to="/quick-entry"
              className={cn(
                "flex flex-col items-center justify-center h-14 min-w-[60px] rounded-lg transition-all active:scale-95",
                location.pathname === '/quick-entry' ? "text-primary" : "text-muted-foreground"
              )}
            >
              <Zap className="h-5 w-5" />
              <span className="text-[10px] mt-1 font-medium">Issue</span>
            </Link>
            <Link
              to="/quick-inward"
              className={cn(
                "flex flex-col items-center justify-center h-14 min-w-[60px] rounded-lg transition-all active:scale-95",
                location.pathname === '/quick-inward' ? "text-primary" : "text-muted-foreground"
              )}
            >
              <ArrowDownToLine className="h-5 w-5" />
              <span className="text-[10px] mt-1 font-medium">Inward</span>
            </Link>
            <Link
              to="/stock"
              className={cn(
                "flex flex-col items-center justify-center h-14 min-w-[60px] rounded-lg transition-all active:scale-95",
                location.pathname === '/stock' ? "text-primary" : "text-muted-foreground"
              )}
            >
              <Package className="h-5 w-5" />
              <span className="text-[10px] mt-1 font-medium">Stock</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex flex-col items-center justify-center h-14 min-w-[60px] rounded-lg text-muted-foreground transition-all active:scale-95"
            >
              <MoreHorizontal className="h-5 w-5" />
              <span className="text-[10px] mt-1 font-medium">More</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default DashboardLayout;
